import { parseCalendarDate } from '../../lib/format';
import type { CreateVersionBody } from './versions-api';

/**
 * PH-28 — the version form's model, with no React in it. Same division as `sprint-form.ts`.
 *
 * 🔴 DATES ARE CALENDAR DATES AND NEVER BECOME `Date` OBJECTS ON THE WAY THROUGH.
 *   `release_date` is a Postgres DATE cast `::text` and arrives as plain `YYYY-MM-DD`; an
 *   `<input type="date">` reads and writes exactly that string. ⇒ The draft holds the API's own
 *   string and hands it back unchanged.
 *   ⛔ `formatCalendarDate(v)` in a date input renders `1 Sept 2026`, which the input CANNOT hold —
 *   it sanitises the value to `''` and the field silently shows empty. `formatInstantDate` is worse:
 *   it would shift the day. `formatDate` no longer exists at all (PH-21).
 */

export interface VersionDraft {
  name: string;
  description: string;
  /** `YYYY-MM-DD`, or `''`. ⚠️ On create this is a PLAN and does not release anything. */
  releaseDate: string;
}

/** The API's own limits (`createVersionBody` in routes/versions.ts), named so they cannot drift. */
export const VERSION_NAME_MAX = 200;
export const VERSION_DESCRIPTION_MAX = 20_000;

/** ⛔ Every field blank. Only the team knows the name, and a release date is genuinely optional. */
export function blankVersionDraft(): VersionDraft {
  return { name: '', description: '', releaseDate: '' };
}

/**
 * Why the API would refuse this draft, or `null`.
 *
 * ⛔ NOT the validation — the API is. What this buys is that a refusal the client can already see
 * does not cost a round trip and come back as `the request body or query was not valid`, the FIXED
 * generic message `validationFailed()` returns. That message cannot echo the input by construction,
 * so a zod refusal reaching the user names no field and is a refusal nobody can act on.
 *
 * ⭐ ONE message at a time, in reading order — five red lines push the button off a 390px screen.
 */
export function versionDraftProblem(draft: VersionDraft): string | null {
  const name = draft.name.trim();
  if (name === '') return 'A version needs a name.';
  if (name.length > VERSION_NAME_MAX) {
    return `A version name is at most ${VERSION_NAME_MAX} characters — this one is ${name.length}.`;
  }
  if (draft.description.trim().length > VERSION_DESCRIPTION_MAX) {
    return `A description is at most ${VERSION_DESCRIPTION_MAX} characters — this one is ${draft.description.trim().length}.`;
  }
  /**
   * ⚠️ Only when something was typed. A blank date is legitimate — `releaseDate` is `.nullish()` on
   * create — but a HALF-TYPED one (`2026-09`) must not be sent: the API's `^\d{4}-\d{2}-\d{2}$`
   * refuses it with the generic message above, which names nothing.
   */
  if (draft.releaseDate !== '' && !parseCalendarDate(draft.releaseDate)) {
    return 'The planned release date must be a real calendar date, or left empty.';
  }
  return null;
}

/**
 * The CREATE body.
 *
 * ⛔ Optional fields are OMITTED, not sent as `null` or `''`. `z.string().max(20_000).nullish()`
 * accepts an empty string quite happily and would store one — a description that is present and
 * blank, which renders as an empty paragraph rather than as no description.
 * ⭐ The name is TRIMMED: `'  '` passes `z.string().min(1)` and would create a version whose card
 * header is an empty gap.
 * ⛔ THERE IS NO `state` KEY, and it is not omitted by accident — `createVersionBody` has no such
 * field. A version is born `unreleased` and the two state changes are their own endpoints.
 */
export function createVersionBody(draft: VersionDraft): CreateVersionBody {
  const description = draft.description.trim();
  return {
    name: draft.name.trim(),
    ...(description === '' ? {} : { description }),
    ...(draft.releaseDate === '' ? {} : { releaseDate: draft.releaseDate }),
  };
}
